// packages/lib/src/schemas.ts
import { z } from 'zod';

// Constants for magic numbers
const MAX_SCORE = 100;
const MIN_SCORE = 0;
const MAX_RATING = 5;
const MIN_RATING = 1;

export const TarotReadingSchema = z.object({
  card: z.object({
    name: z.string(),
    upright: z.boolean(),
    img: z.string(),
  }),
  interpretation: z.object({
    general: z.string(),
    love: z.string(),
    career: z.string(),
  }),
});

export const CompatibilityReportSchema = z
  .object({
    score: z.number().min(MIN_SCORE).max(MAX_SCORE),
    archetype: z.string().optional().default('Cosmic Duo'), // Ensure backward compat if AI misses it, though prompt is strict
    summary: z.string(),
    strengths: z.string(),
    challenges: z.string(),
    tip: z.string(),
  })
  .transform((data) => ({
    ...data,
    advice: data.tip, // Map tip to advice for backward compatibility
  }));

export const DashboardDataSchema = z.object({
  horoscope: z.object({
    love: z.string(),
    career: z.string(),
    wellness: z.string(),
  }),
  insights: z.object({
    luckyNumber: z.number(),
    cosmicColor: z.object({
      name: z.string(),
      hex: z.string(),
    }),
    spiritAnimal: z.string(),
    affirmation: z.string(),
  }),
});

const HOROSCOPE_CATEGORIES = [
  'vibe',
  'love',
  'career',
  'wellness',
  'finance',
  'social',
  'relationships',
  'spirituality',
  'health',
  'emotions',
] as const;

const DEFAULT_CATEGORY: (typeof HOROSCOPE_CATEGORIES)[number] = 'vibe';

const categorySchema = z.preprocess((value) => {
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (HOROSCOPE_CATEGORIES.includes(normalized as (typeof HOROSCOPE_CATEGORIES)[number])) {
      return normalized;
    }
  }
  return;
}, z.enum(HOROSCOPE_CATEGORIES).default(DEFAULT_CATEGORY));

const safeString = (defaultValue = '') =>
  z.preprocess((value) => {
    if (value === null || value === undefined) {
      return;
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return defaultValue;
  }, z.string().default(defaultValue));

const ratingSchema = z
  .preprocess(
    (value) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      const parsed = Number(value);
      if (Number.isNaN(parsed)) {
        return;
      }
      return parsed;
    },
    z
      .number()
      .min(MIN_RATING)
      .max(MAX_RATING)
      .default((MIN_RATING + MAX_RATING) / 2)
  )
  .transform((rating) => {
    const clamped = Math.min(Math.max(rating, MIN_RATING), MAX_RATING);
    return Number.isFinite(clamped) ? clamped : (MIN_RATING + MAX_RATING) / 2;
  });

export const HoroscopeReadingSchema = z.object({
  category: categorySchema,
  title: safeString(),
  rating: ratingSchema,
  summary: safeString(),
  details: safeString(),
});

const safeReadingArray = z.preprocess((value) => {
  if (Array.isArray(value)) {
    return value;
  }
  return [];
}, z.array(HoroscopeReadingSchema));

export const DailyHoroscopeSchema = z.object({
  today: safeReadingArray.default([]),
  tomorrow: safeReadingArray.default([]),
  month: safeReadingArray.default([]),
});
